import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

/**
 * TopProgressBar — A global slim progress bar (NProgress-style) at the top
 * of the viewport. Activates automatically on any in-flight axios request.
 * Uses brand colors and smooth easing for a polished feel.
 *
 * Behavior:
 * - Waits 150ms before showing (avoids flash on fast responses)
 * - Trickles smoothly from 12% to 90% while requests are pending
 * - Snaps to 100% and fades out when all requests complete
 */
export const TopProgressBar = () => {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const activeRequests = useRef(0);
  const trickleTimer = useRef(null);
  const hideTimer = useRef(null);
  const startDelay = useRef(null);
  const isVisible = useRef(false);

  const startTrickle = useCallback(() => {
    if (trickleTimer.current) clearInterval(trickleTimer.current);
    trickleTimer.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        const increment = prev < 30 ? 8 : prev < 60 ? 4 : prev < 80 ? 2 : 0.5;
        return Math.min(prev + increment, 90);
      });
    }, 300);
  }, []);

  const stopTrickle = useCallback(() => {
    if (trickleTimer.current) {
      clearInterval(trickleTimer.current);
      trickleTimer.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    if (startDelay.current) clearTimeout(startDelay.current);
    startDelay.current = setTimeout(() => {
      if (activeRequests.current > 0) {
        isVisible.current = true;
        setVisible(true);
        setProgress(12);
        startTrickle();
      }
    }, 150);
  }, [startTrickle]);

  const done = useCallback(() => {
    if (startDelay.current) {
      clearTimeout(startDelay.current);
      startDelay.current = null;
    }
    stopTrickle();
    if (isVisible.current) {
      setProgress(100);
      hideTimer.current = setTimeout(() => {
        isVisible.current = false;
        setVisible(false);
        setProgress(0);
      }, 400);
    } else {
      setProgress(0);
    }
  }, [stopTrickle]);

  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use(
      (config) => {
        activeRequests.current += 1;
        if (activeRequests.current === 1) start();
        return config;
      },
      (error) => {
        activeRequests.current = Math.max(activeRequests.current - 1, 0);
        if (activeRequests.current === 0) done();
        return Promise.reject(error);
      }
    );

    const resInterceptor = axios.interceptors.response.use(
      (response) => {
        activeRequests.current = Math.max(activeRequests.current - 1, 0);
        if (activeRequests.current === 0) done();
        return response;
      },
      (error) => {
        activeRequests.current = Math.max(activeRequests.current - 1, 0);
        if (activeRequests.current === 0) done();
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
      stopTrickle();
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (startDelay.current) clearTimeout(startDelay.current);
    };
  }, [start, done, stopTrickle]);

  if (!visible && progress === 0) return null;

  return (
    <div
      data-testid="top-progress-bar"
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 z-[9999] h-[3px] pointer-events-none"
    >
      <div
        className="h-full"
        style={{
          width: `${progress}%`,
          opacity: visible ? 1 : 0,
          background: 'linear-gradient(90deg, var(--brand) 0%, var(--accent-gold) 50%, var(--brand-light) 100%)',
          boxShadow: '0 0 8px var(--brand), 0 0 4px var(--brand)',
          transition: progress === 100
            ? 'width 200ms ease-out, opacity 400ms ease-out 200ms'
            : 'width 300ms ease-out',
        }}
      />
    </div>
  );
};
